import { onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/auth'

export function useSessionTracker() {
  const authStore = useAuthStore()

  // Track meaningful user activity events (excluding passive mousemove)
  // These events represent actual user interactions:
  // - mousedown: User clicks or starts interaction
  // - keypress/keydown: User types or uses keyboard
  // - scroll: User actively scrolls content
  // - touchstart: User touches screen on mobile
  // - click: User clicks elements
  // - focus: User focuses on form elements
  // - input/change: User enters data in forms
  const activityEvents = [
    'mousedown',
    'keypress',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'focus',
    'input',
    'change',
  ]

  const updateActivity = () => {
    if (authStore.isAuthenticated) {
      authStore.updateActivity()

      // Trigger warning setup in session warning composable
      const sessionWarningEvent = new CustomEvent('sessionActivity')
      window.dispatchEvent(sessionWarningEvent)
    }
  }

  // Throttle activity updates to avoid excessive calls
  let throttleTimeout: number | null = null
  const throttledUpdateActivity = () => {
    if (throttleTimeout) return

    throttleTimeout = window.setTimeout(() => {
      updateActivity()
      throttleTimeout = null
    }, 5000) // Update activity at most every 5 seconds
  }

  onMounted(() => {
    // Add event listeners for activity tracking
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, true)
    })

    // Initialize auth state if authenticated
    authStore.initializeAuth()
  })

  onUnmounted(() => {
    // Clean up event listeners
    activityEvents.forEach((event) => {
      document.removeEventListener(event, throttledUpdateActivity, true)
    })

    // Clear throttle timeout
    if (throttleTimeout) {
      clearTimeout(throttleTimeout)
    }
  })

  return {
    updateActivity: throttledUpdateActivity,
  }
}
